import { Control, Controller, RegisterOptions } from "react-hook-form";
import { useMemo, useState } from "react";

import { isValidUrl, URL_PROTOCOLS } from "@/utils/generic";

import { Globe, Plus, Trash2 } from "lucide-react";
import { NoxusFormLabel } from "./NoxusFormLabel";
import { NumberInput } from "./NumberInputField";
import { Button } from "../shadcn/button";
import { Input } from "../shadcn/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../shadcn/select";
import { ErrorMessage } from "./ErrorMessage";
import { NoxusEmptyBlock } from "../UI/NoxusEmptyBlock";
import { NoxusItem } from "../UI/NoxusItem";

interface IWebsiteEntry {
  url: string;
  depth: number;
  max_urls: number;
}

const DEPTH_OPTIONS = [
  { value: "0", label: "Single page" },
  { value: "1", label: "1 level deep" },
  { value: "2", label: "2 levels deep" },
];

const DEPTH_DESCRIPTIONS: Record<number, string> = {
  0: "Single page",
  1: "Crawls 1 level deep",
  2: "Crawls 2 levels deep",
};

const DEFAULT_MAX_URLS = 50;
const MIN_MAX_URLS = 1;
const MAX_MAX_URLS = 500;

interface WebsiteScrapeInputProps {
  title?: string;
  value: IWebsiteEntry[];
  tooltip?: string;
  maxDepth?: number;
  onChange: (value: IWebsiteEntry[]) => void;
  name?: string;
  isDisabled?: boolean;
}

const WebsiteScrapeInput = (props: WebsiteScrapeInputProps) => {
  const { value, onChange, isDisabled, maxDepth = 3 } = props;

  const [isInvalidUrl, setIsInvalidUrl] = useState(false);
  const [url, setUrl] = useState("");
  const [depth, setDepth] = useState("0");
  const [maxUrls, setMaxUrls] = useState<number>(DEFAULT_MAX_URLS);

  const handleDeleteWebsite = (website: IWebsiteEntry) => {
    if (isDisabled) return;
    onChange(value.filter((w) => w.url !== website.url));
  };

  const handleAddWebsite = () => {
    if (isDisabled) return;
    setIsInvalidUrl(false);
    const notAddedYet = !value.some((website) => website.url === url);

    const isValid = isValidUrl(url, {
      allowed_protocols: [URL_PROTOCOLS.HTTP, URL_PROTOCOLS.HTTPS],
    });

    if (!isValid) {
      setIsInvalidUrl(true);
      return;
    }
    if (url && notAddedYet) {
      onChange([
        ...value,
        { url, depth: parseInt(depth, 10), max_urls: maxUrls },
      ]);
      setUrl("");
      setDepth("0");
      setMaxUrls(DEFAULT_MAX_URLS);
    }
  };

  const depthOptions = useMemo(
    () => DEPTH_OPTIONS.filter((o) => parseInt(o.value, 10) <= maxDepth),
    [maxDepth]
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Input row + error (NoxusInput spacing: gap-1.5 between field and error) */}
      <div className="flex flex-col gap-1.5">
        <div className="flex w-full gap-2 items-end">
          <div className="flex-1 min-w-0">
            <NoxusFormLabel label="URL" name={props.name} />
            <div className="relative">
              <Globe
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <Input
                placeholder="https://example.com"
                className="pl-8"
                aria-invalid={isInvalidUrl}
                onChange={(e) => {
                  setIsInvalidUrl(false);
                  setUrl(e.target.value);
                }}
                value={url}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    e.stopPropagation();
                    handleAddWebsite();
                  }
                }}
                disabled={isDisabled}
              />
            </div>
          </div>

          <div className="w-[140px] shrink-0">
            <NoxusFormLabel
              label="Depth"
              tooltip="How many levels of links the crawler follows from the starting page"
            />
            <Select
              value={depth}
              onValueChange={setDepth}
              disabled={isDisabled}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {depthOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="w-[100px] shrink-0">
            <NoxusFormLabel
              label="Max pages"
              tooltip="Maximum pages to crawl for this URL"
            />
            <NumberInput
              value={maxUrls}
              onChange={setMaxUrls}
              min={MIN_MAX_URLS}
              max={MAX_MAX_URLS}
              isDisabled={isDisabled}
            />
          </div>

          <Button
            variant="outline"
            size="icon"
            className="shrink-0"
            onClick={handleAddWebsite}
            disabled={!url || url.length === 0 || isDisabled}
            aria-label="Add URL"
          >
            <Plus size={14} />
          </Button>
        </div>

        {isInvalidUrl && (
          <ErrorMessage errorMessage="Please enter a valid URL (must start with http or https)" />
        )}
      </div>

      {/* URL list or empty state */}
      {value.length > 0 ? (
        <div className="flex flex-col gap-1.5 max-h-[280px] overflow-y-auto thin-scrollbars">
          {value.map((website, index) => (
            <NoxusItem
              key={`website-${index}`}
              className="bg-background2"
              iconClassName="bg-white"
              icon={Globe}
              title={website.url}
              description={`${
                DEPTH_DESCRIPTIONS[website.depth] ?? `Depth ${website.depth}`
              } · ${website.max_urls} max pages`}
              actions={
                !isDisabled && (
                  <Button
                    variant="ghost"
                    size="iconsm"
                    onClick={() => handleDeleteWebsite(website)}
                    aria-label="Remove URL"
                  >
                    <Trash2 strokeWidth={1.33} />
                  </Button>
                )
              }
            />
          ))}
        </div>
      ) : (
        <div className="rounded-lg border border-border">
          <NoxusEmptyBlock
            size="sm"
            icon={Globe}
            title="No websites added yet"
            description="Enter a URL above and click + to add"
          />
        </div>
      )}
    </div>
  );
};

interface IWebsiteScrapeField {
  name: string;
  tooltip?: string;
  label?: string;
  rules?: RegisterOptions;
  control: Control<any>;
  defaultValue?: IWebsiteEntry[];
  isDisabled?: boolean;
  maxDepth?: number;
}

export const WebsiteScrapeField = (props: IWebsiteScrapeField) => {
  return (
    <Controller
      control={props.control}
      name={props.name}
      rules={props.rules}
      defaultValue={props.defaultValue}
      render={({ field: { onChange, value } }) => {
        return (
          <WebsiteScrapeInput
            name={props.name}
            value={value || []}
            title={props.label}
            tooltip={props.tooltip}
            maxDepth={props.maxDepth}
            onChange={onChange}
            isDisabled={props.isDisabled}
          />
        );
      }}
    />
  );
};
